import type { Permission } from '@shared/lib';
import type { LoginData } from './schemas';
import type { MeData } from './schemas';
import type { SessionUser } from '../model/types';

/** Pull the JWT out of a login `data` payload (`token`, or `access_token` for forward-compat). */
export function extractToken(data: LoginData): string | null {
  return data.token ?? data.access_token ?? null;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : undefined;
}

/** First message from an `errors` value, whether it's `[{message}]`, `["..."]`, or `"..."`. */
function firstErrorMessage(errors: unknown): string | null {
  if (typeof errors === 'string') return errors;
  if (!Array.isArray(errors) || errors.length === 0) return null;
  const first: unknown = (errors as unknown[])[0];
  if (typeof first === 'string') return first;
  const message = asRecord(first)?.message;
  return typeof message === 'string' ? message : null;
}

/**
 * Best-effort human-readable message from a backend error payload. Handles the several shapes
 * the API uses: `data.errors[0].message`, top-level `errors[0].message`, an `errors`/`message`
 * string, or a plain `message`. Returns null if nothing usable is found.
 */
export function extractApiErrorMessage(payload: unknown): string | null {
  const root = asRecord(payload);
  if (!root) return null;
  const data = asRecord(root.data);

  return (
    firstErrorMessage(data?.errors) ??
    firstErrorMessage(root.errors) ??
    (typeof data?.message === 'string' ? data.message : null) ??
    (typeof root.message === 'string' ? root.message : null)
  );
}

/** Anti-corruption: `GET /api/v1/auth/me` response data → clean domain session user. */
export function mapMeToSession(data: MeData): SessionUser {
  const u = data.user;
  const fullName =
    u.full_name?.trim() ?? [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  const roleName = data.role?.name;
  const displayName = [fullName, u.email, u.phone_number].find((v) => v) ?? 'Riloma User';
  return {
    id: u._id,
    name: displayName,
    email: u.email ?? '',
    roles: roleName ? [roleName] : [],
    permissions: (data.role?.permissions ?? []) as Permission[],
    // ISO code is not in the /me response; the currency is resolved by defaultCurrencyId.
    defaultCurrency: 'USD',
    defaultCurrencyId: data.user_config?.default_currency_id ?? u.currency_id ?? '',
    avatarUrl: u.profile_picture_url ?? '',
    phone: u.phone_number ?? '',
    timeZone: u.timezone ?? '',
    plan: '',
    autoAttachLocation: data.user_config?.auto_attach_location ?? true,
  };
}
